"use client";

import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/shadcn/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import Chip from "@/components/global/ui/Chip";
import Image from "@/helpers/Image";
import { cn } from "@/lib/utils";
import lines from "@/public/decos/lines-deco.svg";
import type { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";

const TRANSITION_EASING = [0.22, 1, 0.36, 1] as const;
const TRANSITION_DURATION = 0.6;
const AUTOPLAY_DELAY = 4000;

const HERO_BG_INITIAL = {
  background: "radial-gradient(176.45% 100% at 50% 0%, #050200 50%, #F50 80%, #FFF 100%)",
};
const HERO_BG_ANIMATE = {
  background: "radial-gradient(176.45% 100% at 50% 0%, #050200 18.47%, #F50 64.16%, #FFF 100%)",
};
const HERO_BG_TRANSITION = { duration: 1.8, ease: [...TRANSITION_EASING], delay: 0 };
const REVEAL_HERO_CHIP = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
} as const;
const REVEAL_HERO_TEXT = {
  initial: { opacity: 0, y: 32 },
  animate: { opacity: 1, y: 0 },
} as const;
const REVEAL_HERO_CTA = {
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
} as const;
const REVEAL_HERO_BUTTON = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
} as const;
const REVEAL_HERO_IMAGE_WRAPPER = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
} as const;
const HERO_CHIP_TRANSITION = { duration: 0.7, ease: [...TRANSITION_EASING], delay: 0.1 };
const HERO_TEXT_TRANSITION = { duration: 0.8, ease: [...TRANSITION_EASING], delay: 0.25 };
const HERO_CTA_TRANSITION = { duration: 0.7, ease: [...TRANSITION_EASING], delay: 0.45 };
const HERO_BUTTON_TRANSITION_BASE = { duration: TRANSITION_DURATION, ease: [...TRANSITION_EASING] };
const HERO_IMAGE_WRAPPER_TRANSITION = { duration: 0.9, ease: [...TRANSITION_EASING], delay: 0.7 };
const HERO_IMAGE_VARIANTS = {
  enter: (dir: number) => ({ x: `${dir * 30}%`, opacity: 0 }),
  center: { x: "0%", opacity: 1 },
  exit: (dir: number) => ({ x: `${dir * -15}%`, opacity: 0 }),
};
const HERO_IMAGE_TRANSITION = {
  x: { duration: TRANSITION_DURATION, ease: [...TRANSITION_EASING] },
  opacity: { duration: TRANSITION_DURATION, ease: [...TRANSITION_EASING] },
};

const ChevronLeft = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none">
    <rect
      width="32"
      height="32"
      transform="matrix(-1 0 0 1 32 0)"
      fill="white"
      fillOpacity="0.01"
      style={{ mixBlendMode: "multiply" }}
    />
    <path
      d="M9.99844 16L19.9984 26L21.3984 24.6L12.7984 16L21.3984 7.4L19.9984 6L9.99844 16Z"
      fill="black"
    />
  </svg>
);

function HeroSectionDesktop({ data }: { data: PageHomeSchema }) {
  const { heroChip, heroText, heroCTA, heroItems } = data;
  const [activeItemIndex, setActiveItemIndex] = React.useState(0);
  const [direction, setDirection] = React.useState(0);

  const handleItemChange = (newIndex: number) => {
    setDirection(newIndex > activeItemIndex ? 1 : -1);
    setActiveItemIndex(newIndex);
  };

  return (
    <section className="relative hidden w-full overflow-x-clip pt-[16.25rem] lg:block">
      <div className="absolute top-0 left-1/2 h-[60%] w-full -translate-x-1/2">
        <motion.div
          className="relative h-full w-full"
          initial={HERO_BG_INITIAL}
          animate={HERO_BG_ANIMATE}
          transition={HERO_BG_TRANSITION}>
          <div className="pointer-events-none absolute inset-0 bg-[url('/noise.svg?v=4')] [mask-image:linear-gradient(to_bottom,black_50%,transparent_95%)] bg-repeat opacity-50" />
        </motion.div>
      </div>

      <div className="absolute top-0 left-[27.5rem] h-full w-[.125rem] bg-white/10" />
      <div className="absolute top-0 right-[27.5rem] h-full w-[.125rem] bg-white/10" />

      <div className="relative z-[5] mx-auto flex w-[min(99.375rem,100%)] flex-col items-center pb-[8.125rem]">
        {heroChip && (
          <motion.div
            {...REVEAL_HERO_CHIP}
            transition={HERO_CHIP_TRANSITION}>
            <Chip
              text={heroChip.text}
              animatedIcon
              spinnerVariant="magnify"
              className="mb-[2rem] prose-p:text-white"
            />
          </motion.div>
        )}

        {heroText && (
          <motion.div
            className="richtext richtext-center prose-h1:bg-gradient-to-b prose-h1:from-white prose-h1:to-[#CFCFCF] prose-h1:bg-clip-text prose-h1:text-[8rem] prose-h1:leading-[110%] prose-h1:font-normal prose-h1:tracking-normal prose-h1:text-transparent prose-h1:[text-wrap-style:balance] prose-p:max-w-[88.625rem] prose-p:text-[2.25rem] prose-p:text-white/80"
            {...REVEAL_HERO_TEXT}
            transition={HERO_TEXT_TRANSITION}>
            <PortableText
              value={heroText}
              components={richTextComponent}
            />
          </motion.div>
        )}

        {heroCTA && (
          <motion.div
            className="mx-auto mt-[4rem] w-fit"
            {...REVEAL_HERO_CTA}
            transition={HERO_CTA_TRANSITION}>
            <CTAButton
              data={heroCTA}
              className=""
            />
          </motion.div>
        )}
      </div>

      <div className="relative z-[5] flex flex-col gap-[3rem]">
        <div className="absolute top-[6.125rem] -right-[1rem] z-[5]">
          <Image
            src={lines}
            alt=""
          />
        </div>
        <div className="absolute top-[6.125rem] -left-[1rem] z-[5]">
          <Image
            src={lines}
            alt=""
          />
        </div>
        <div className="flex w-full items-center justify-center border-y border-y-white/30">
          <div className="mx-auto flex gap-[.5rem] overflow-y-clip border-x border-x-white/30 px-[.5rem] py-[0.625rem]">
            {heroItems?.map((item, index) => (
              <motion.button
                key={index}
                type="button"
                onClick={() => handleItemChange(index)}
                {...REVEAL_HERO_BUTTON}
                transition={{ ...HERO_BUTTON_TRANSITION_BASE, delay: 0.55 + index * 0.08 }}
                className={cn(
                  "flex justify-center items-center rounded-full h-[4.75rem] w-fit py-[1rem] px-[2rem] transition-colors cursor-pointer border",
                  activeItemIndex === index ? "bg-white border-white/0" : "bg-white/0 border-white/40",
                )}>
                <p
                  className={cn(
                    "m-0 leading-[110%] text-[2rem] transition-colors",
                    activeItemIndex === index ? "text-black" : "text-white/80",
                  )}>
                  {item.title}
                </p>
              </motion.button>
            ))}
          </div>
        </div>
        {heroItems && heroItems.length > 0 && (
          <motion.div
            className="relative mx-auto w-[min(177.5rem,100%)] overflow-hidden"
            {...REVEAL_HERO_IMAGE_WRAPPER}
            transition={HERO_IMAGE_WRAPPER_TRANSITION}>
            {/* Invisible sizer to maintain container height */}
            <div
              className="pointer-events-none invisible relative"
              aria-hidden="true">
              {heroItems[activeItemIndex]?.image && (
                <Image
                  className="w-full"
                  src={heroItems[activeItemIndex].image}
                  alt=""
                  quality={100}
                />
              )}
            </div>

            <AnimatePresence
              initial={false}
              mode="sync"
              custom={direction}>
              <motion.div
                key={activeItemIndex}
                custom={direction}
                className="absolute inset-0"
                variants={HERO_IMAGE_VARIANTS}
                initial="enter"
                animate="center"
                exit="exit"
                transition={HERO_IMAGE_TRANSITION}>
                {heroItems[activeItemIndex]?.image && (
                  <Image
                    className="relative z-[1] w-full"
                    src={heroItems[activeItemIndex].image}
                    alt={heroItems[activeItemIndex].title}
                    quality={100}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </section>
  );
}

function HeroSectionMobile({ data }: { data: PageHomeSchema }) {
  const { heroChip, heroText, heroCTA, heroItems } = data;
  const [activeItemIndex, setActiveItemIndex] = React.useState(0);
  const [carouselApi, setCarouselApi] = React.useState<CarouselApi>();
  const autoplay = React.useRef(Autoplay({ delay: AUTOPLAY_DELAY, stopOnInteraction: false, stopOnMouseEnter: true }));

  React.useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => {
      setActiveItemIndex(carouselApi.selectedScrollSnap());
    };
    carouselApi.on("select", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  return (
    <section className="relative w-full overflow-x-clip pt-[14rem] lg:hidden">
      <div
        className="absolute top-0 left-1/2 h-[146.59263rem] w-[266.4515rem] -translate-x-1/2"
        style={{
          background: "radial-gradient(176.45% 100% at 50% 0%, #050200 18.47%, #F50 64.16%, #FFF 100%)",
        }}>
        <div className="pointer-events-none absolute inset-0 bg-[url('/noise.svg?v=4')] [mask-image:linear-gradient(to_bottom,black_50%,transparent_95%)] bg-repeat opacity-50" />
      </div>

      <div className="relative z-[5] mx-auto flex w-[min(99.375rem,100%)] flex-col items-center px-[3rem]">
        {heroChip && (
          <div>
            <Chip
              text={heroChip.text}
              spinnerVariant="magnify"
              className="mb-[2.5rem] gap-[1.5rem] prose-p:text-white"
            />
          </div>
        )}

        {heroText && (
          <div className="richtext richtext-center prose-h1:mb-[2rem] prose-h1:bg-gradient-to-b prose-h1:from-white prose-h1:to-[#CFCFCF] prose-h1:bg-clip-text prose-h1:text-[5.5rem] prose-h1:leading-[110%] prose-h1:font-normal prose-h1:tracking-normal prose-h1:text-transparent prose-h1:[text-wrap-style:balance] prose-p:max-w-[88.625rem] prose-p:text-[2.25rem] prose-p:text-white/80">
            <PortableText
              value={heroText}
              components={richTextComponent}
            />
          </div>
        )}

        {heroCTA && (
          <div className="mx-auto mt-[4rem] w-full">
            <CTAButton
              data={heroCTA}
              className=""
            />
          </div>
        )}
      </div>

      {heroItems && heroItems.length > 0 && (
        <div className="relative z-[5] mt-[4rem] flex flex-col gap-[3rem]">
          {/* Pills — horizontally scrollable */}
          <div className="overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex w-max gap-[0.5rem] px-[3rem]">
              {heroItems.map((item, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    carouselApi?.scrollTo(index);
                    autoplay.current.reset();
                  }}
                  className={cn(
                    "flex justify-center items-center rounded-full h-[4.75rem] w-fit py-[1rem] px-[2rem] cursor-pointer border whitespace-nowrap",
                    activeItemIndex === index ? "bg-white border-white/0" : "bg-white/0 border-white/40",
                  )}>
                  <p
                    className={cn(
                      "m-0 leading-[110%] text-[2rem]",
                      activeItemIndex === index ? "text-black" : "text-white/80",
                    )}>
                    {item.title}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Embla carousel */}
          <Carousel
            setApi={setCarouselApi}
            opts={{ loop: true, align: "start" }}
            plugins={[autoplay.current]}
            className="w-full pl-[1rem]">
            <CarouselContent className="">
              {heroItems.map((item, i) => (
                <CarouselItem
                  key={i}
                  className="relative ml-[2rem] basis-full overflow-hidden pl-0">
                  {item.image && (
                    <Image
                      className="z-[1] !w-full"
                      src={item.image}
                      alt={item.title}
                      quality={100}
                    />
                  )}
                </CarouselItem>
              ))}
            </CarouselContent>

            {/* Progress bar + prev/next */}
            <div className="mt-[2rem] flex items-center gap-[2rem] px-[3rem]">
              <button
                type="button"
                onClick={() => carouselApi?.scrollPrev()}
                disabled={activeItemIndex === 0}
                className="flex aspect-square h-[7rem] w-[7rem] shrink-0 items-center justify-center gap-[1rem] rounded-[1rem] border border-black p-[1rem] disabled:opacity-30"
                aria-label="Previous">
                {ChevronLeft}
              </button>

              <div className="flex flex-1 gap-[0.75rem]">
                {heroItems.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      carouselApi?.scrollTo(i);
                      autoplay.current.reset();
                    }}
                    className="h-[0.375rem] flex-1 overflow-hidden rounded-full bg-black/10"
                    aria-label={`Go to slide ${i + 1}`}>
                    <div
                      className="h-full rounded-full bg-[#FF5500]"
                      style={{ width: i <= activeItemIndex ? "100%" : "0%" }}
                    />
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => carouselApi?.scrollNext()}
                disabled={activeItemIndex === heroItems.length - 1}
                className="flex aspect-square h-[7rem] w-[7rem] shrink-0 rotate-180 items-center justify-center gap-[1rem] rounded-[1rem] border border-black p-[1rem] disabled:opacity-30"
                aria-label="Next">
                {ChevronLeft}
              </button>
            </div>
          </Carousel>
        </div>
      )}
    </section>
  );
}

export default function HeroSection({ data }: { data: PageHomeSchema }) {
  return (
    <>
      <HeroSectionDesktop data={data} />
      <HeroSectionMobile data={data} />
    </>
  );
}
